// src/components/dashboards/composition/openControlSpec.ts
//
// The open controls that steer no field (ADR-0350, plan 2026-09-29 controls
// A4): the date range control and the button. Their stored shape is the
// backend's `controls.open_control_shape`: no `dimension`, no `selection`,
// a button carries `button: {action: "link" | "reset_filters", url?}`. Field
// controls are `openFieldControlOfSpec` in blocks.ts. Read defensively: a
// copy or a seed writes specs without the route's shape check.
import type { TimeRangePreset } from "@/utils/comparisonWindow";
import { isControlSpec, openFieldControlOfSpec } from "./blocks";

export type ButtonControl = {
  kind: "button";
  label: string | null;
  action: "link" | "reset_filters";
  /** The link's address; null for a reset button. */
  url: string | null;
};

export type DateRangeControl = {
  kind: "date_range";
  label: string | null;
  /** The preset it rests on (controls A4b); null = it follows the dashboard
   *  period. Validated against the picker's presets by the backend; read
   *  here as any non-empty string, the period helper drops one it cannot
   *  resolve. */
  default: TimeRangePreset | null;
};

function controlDict(spec: Record<string, unknown> | null | undefined): Record<string, unknown> | null {
  if (!isControlSpec(spec)) return null;
  const control = spec?.control;
  return typeof control === "object" && control !== null && !Array.isArray(control)
    ? (control as Record<string, unknown>)
    : null;
}

function labelOf(control: Record<string, unknown>): string | null {
  return typeof control.label === "string" && control.label !== "" ? control.label : null;
}

export function dateRangeControlOfSpec(spec: Record<string, unknown> | null | undefined): DateRangeControl | null {
  const control = controlDict(spec);
  if (control?.kind !== "date_range") return null;
  const preset = typeof control.default === "string" && control.default !== "" ? (control.default as TimeRangePreset) : null;
  return { kind: "date_range", label: labelOf(control), default: preset };
}

export function buttonControlOfSpec(spec: Record<string, unknown> | null | undefined): ButtonControl | null {
  const control = controlDict(spec);
  if (control?.kind !== "button") return null;
  const button = control.button as { action?: unknown; url?: unknown } | null | undefined;
  const action = button?.action;
  if (action !== "link" && action !== "reset_filters") return null;
  return {
    kind: "button",
    label: labelOf(control),
    action,
    url: action === "link" && typeof button?.url === "string" ? button.url : null,
  };
}

/** The link a button may open: an absolute `https:` address with a host and
 *  nothing else (the backend's `safe_link_url` rule, checked again here
 *  because copy and seed paths write specs without it). Null otherwise, so
 *  a `javascript:` or `data:` address never reaches an `href`. */
export function safeButtonHref(raw: string | null | undefined): string | null {
  if (typeof raw !== "string" || raw === "") return null;
  // No whitespace, control characters or backslashes anywhere (the URL
  // parser would silently strip or normalise them).
  for (const ch of raw) {
    const code = ch.charCodeAt(0);
    if (code <= 0x20 || code === 0x7f || ch === "\\") return null;
  }
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  return url.protocol === "https:" && url.hostname !== "" ? url.href : null;
}

/** Is this spec an OPEN control (a field control, a date range or a button):
 *  the controls an author configures in the control settings panel (plan
 *  2026-09-29 controls A5). A register control (the attribution model) is
 *  not: nothing configures it. */
export function isOpenControlSpec(spec: Record<string, unknown> | null | undefined): boolean {
  return Boolean(openFieldControlOfSpec(spec) || dateRangeControlOfSpec(spec) || buttonControlOfSpec(spec));
}
